<template>
    <divider-generic
    icon="calendar"
    :applyBorder="false"
    :title="t('bankassurance.subscribe.travel.step3.travelDaysTitle')"/>
    <text-block-generic
    :params="{
        classes: 'capitalize',
        label: t('bankassurance.subscribe.travel.step3.startDateLabel'),
        content: getDate(formData.dataInicio)
    }"/>
    <text-block-generic
    :params="{
        classes: 'capitalize',
        label: t('bankassurance.subscribe.travel.step3.endDateLabel'),
        content: getDate(formData.dataFim)
    }"/>
    <divider-generic icon="identification" :title="t('bankassurance.subscribe.travel.step3.insuredPersonsDataTitle', formData.insuredPersonList.length)"/>
    <selectors-checkbox
    :label="t('bankassurance.subscribe.travel.step3.isHolderLabel')"
    :selected="isHolder"
    @clicked="emit('changeHolder')"/>
    <container-generic
    v-for="(insuredPerson, index) in formData.insuredPersonList"
    :id="`insuredPersonContainer_${index}`"
    :key="insuredPerson"
    :title="insuredPerson.name ? insuredPerson.name : t('bankassurance.subscribe.travel.step3.personTitle', { number: index + 1})"
    :hasError="insuredPersonsContainers[index].hasError"
    @headerClicked="insuredPersonsContainers[index].isOpen = $event">
        <insured-person-block
        :isHolder="isHolder && index === 0"
        :index="index"
        :insuredPerson="insuredPerson"
        :showInsuredPersonErrors="showInsuredPersonErrors"
        @insuredPersonDataChange="emit('insuredPersonDataChange', $event)"
        @addStepValidationError="emit('addStepValidationError', $event)"
        @removeStepValidationError="emit('removeStepValidationError', $event)"
        @resetShowInsuredPersonErrorsProp="emit('resetShowInsuredPersonErrorsProp')"
        @scrollTo="scrollToHandler($event, index)"
        @showOverlayLoader="emit('showOverlayLoader')"
        @hideOverlayLoader="emit('hideOverlayLoader')"
        @showError="emit('showError', $event)"
        @showContainerError="insuredPersonsContainers[index].hasError = $event"/>
    </container-generic>
    <divider-generic icon="people" :title="t('bankassurance.subscribe.travel.step3.beneficiarysDataTitle')"/>
    <infobox-generic
    :label="t('bankassurance.subscribe.travel.step3.beneficiaryExtraInfo')"/>
    <container-generic
    v-for="(beneficiary, index) in formData.beneficiaryList"
    :key="beneficiary"
    :title="beneficiary.name ? beneficiary.name : t('bankassurance.subscribe.travel.step3.beneficiaryTitle', { number: index + 1 })"
    :options="[{
        id: index,
        text: t('bankassurance.subscribe.travel.step3.deleteLabel'),
        variant: 'negative',
        icon: 'delete'
    }]"
    @moreOptionsClicked="emit('removeBeneficiary', $event)">
        <input-generic
        :params="{
            labelText: t('bankassurance.subscribe.travel.step3.beneficiaryNameLabel'),
            placeholder: t('bankassurance.subscribe.travel.step3.beneficiaryNamePlaceholder'),
            maxLength: 42
        }"
        :inputValue="beneficiary.name"
        @input="[emit('beneficiaryDataChange', { field: 'name', inputValue: $event, index: index})]"/>
    </container-generic>
    <button-tertiary
    v-if="formData.beneficiaryList.length < 9"
    icon="plus"
    :class="{'mobile': getMobileAppState}"
    :label="t('bankassurance.subscribe.travel.step3.addBeneficiaryLabel')"
    @clicked="emit('addBeneficiary')"/>
    <divider-generic icon="trip" :title="t('bankassurance.subscribe.travel.step3.travelDataTitle')"/>
    <!-- Travel Start -->
    <text-block-generic
    :params="{
        label: t('bankassurance.subscribe.travel.step3.startLabel'),
        content: getDomainData('Country').find(ele => ele.code === formData.origemViagem).description
    }"/>
    <!-- Travel End -->
    <dropdown-generic
    id="destinoViagem"
    :params="destinoViagemParams"
    :options="getDomainData('Country').map(ele => {
        return { id: ele.code, text: ele.description }
    })"
    :selectedOption="formData.destinoViagem"
    @clicked="[emit('formDataChange', { field: 'destinoViagem', inputValue: $event }), clearFieldError('destinoViagemParams')]"/>
    <!-- Protection Plan -->
    <text-block-generic
    :params="{
        label: t('bankassurance.subscribe.travel.step3.travelProtectionLabel'),
        content: getDomainData('TravelProtectionPlan').find(ele => ele.code === formData.planoProtecaoViagem).description
    }"/>
    <!-- Capital Scale -->
    <text-block-generic
    :params="{
        label: t('bankassurance.subscribe.travel.step3.capitalScaleLabel'),
        content: getDomainData('TravelCapitalSscale').find(ele => ele.code === formData.escalaoCapitalViagem).description
    }"/>
    <!-- Covid in Travel -->
    <text-block-generic
    :params="{
        label: t('bankassurance.subscribe.travel.step3.covidInTravelLabel'),
        content: formData.covidViagem === 'S' ? t('bankassurance.subscribe.travel.step3.yes') : t('bankassurance.subscribe.travel.step3.no')
    }"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import SelectorsCheckbox from '@/components/generic/selectors/checkbox/Checkbox'
import ContainerGeneric from '@/components/generic/containers/Generic'
import InsuredPersonBlock from '@/components/features/bankassurance/subscribe/travel/InsuredPersonBlock'
import InputGeneric from '@/components/generic/inputs/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'

/* Vue */
import { ref, watch, onBeforeMount } from 'vue'

/* Assets */
import { getDate } from '@/assets/scripts/dateBuilders'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['formDataChange', 'insuredPersonDataChange', 'formErrorsCleared', 'resetShowMandatoryErrorsProp',
                        'resetShowInsuredPersonErrorsProp', 'addStepValidationError', 'removeStepValidationError', 'addBeneficiary',
                        'removeBeneficiary', 'beneficiaryDataChange', 'scrollTo', 'showOverlayLoader', 'hideOverlayLoader', 'showError', 'changeHolder'])

/* mapActions & mapGetters */
const {	getDomainData, getHolderData } = mapGetters(moduleMapper.BANKASSURANCE_SUBSCRIBE)
const { getMobileAppState } = mapGetters()

/* props received
@formData { Object } - object with info for our current form
*/
const props = defineProps({
    formData: Object,
    mandatoryList: Array,
    isHolder: Boolean,
    showInsuredPersonErrors: Boolean,
    showMandatoryErrors: Boolean
})

/**********************************************************************
*                          Destino Viagem                             *
**********************************************************************/
const destinoViagemParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.endLabel'),
	placeholder: t('bankassurance.subscribe.travel.step3.endPlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

watch(() => props.isHolder, newValue => {
	if (props.isHolder) {
        emit('insuredPersonDataChange', { field: 'name', inputValue: getHolderData.value.name, index: 0 })
        emit('insuredPersonDataChange', { field: 'identification', inputValue: getHolderData.value.identification, index: 0 })
        emit('insuredPersonDataChange', { field: 'birthDate', inputValue: getHolderData.value.birthDate, index: 0 })
        emit('insuredPersonDataChange', { field: 'fiscalIdentification', inputValue: getHolderData.value.fiscalIdentification, index: 0 })
        emit('insuredPersonDataChange', { field: 'contact', inputValue: getHolderData.value.contact, index: 0 })
        emit('insuredPersonDataChange', { field: 'email', inputValue: getHolderData.value.email.trim(), index: 0 })
        emit('insuredPersonDataChange', { field: 'gender', inputValue: getHolderData.value.gender.code, index: 0 })
        emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'postalCode', inputValue: getHolderData.value.insuranceEntityAddress.postalCode.code, index: 0 })
        emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'province', inputValue: getHolderData.value.insuranceEntityAddress.province.code, index: 0 })
        emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'country', inputValue: getHolderData.value.insuranceEntityAddress.country.code, index: 0 })
    } else {
        emit('insuredPersonDataChange', { field: 'name', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'identification', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'birthDate', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'fiscalIdentification', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'contact', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'email', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'gender', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'postalCode', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'province', inputValue: '', index: 0 })
        emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'country', inputValue: 'ANG', index: 0 })
    }
})

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
    destinoViagemParams: destinoViagemParams.value
})

const insuredPersonsContainers = ref([])

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

	if (mandatoryFieldsEmptyList.length) {
        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    }
}

const scrollToHandler = (event, index) => {
    if (!event) {
        if (!insuredPersonsContainers.value[index].isOpen && insuredPersonsContainers.value[index].hasError) {
            emit('scrollTo', getElementOffsetTopPosition(`insuredPersonContainer_${index}`))
        }
    } else {
        emit('scrollTo', event)
    }
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

onBeforeMount(() => {
    insuredPersonsContainers.value = props.formData.insuredPersonList.map(person => {
        return { isOpen: true, hasError: false }
    })
})

</script>

<style lang="scss" scoped>

</style>
